import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import api from '../../services/api'
import PrivatePageSection from '../../components/layout/PrivatePageSection'
import { normalizeArray, pickValue, toSafeText } from '../../utils/defensiveData'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material'

const ClientDetail = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { clientId } = useParams()

  const seededClient = location?.state?.client || null

  const [client, setClient] = useState(seededClient)
  const [products, setProducts] = useState([])
  const [usersCount, setUsersCount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchClientDetail = async () => {
      setLoading(true)
      setError('')

      try {
        const clientResponse = await api.get(`/clients/${clientId}`)
        const clientPayload = clientResponse?.data || null
        setClient(clientPayload)

        const [productsResult, usersResult] = await Promise.allSettled([
          api.get('/products'),
          api.get('/users'),
        ])

        if (productsResult.status === 'fulfilled') {
          const allProducts = normalizeArray(productsResult.value?.data)
          const associatedProducts = allProducts.filter((product) => {
            const productClientId = pickValue(product, 'client_id', 'clientId', '')
            return String(productClientId) === String(clientId)
          })
          setProducts(associatedProducts)
        } else {
          setProducts([])
        }

        if (usersResult.status === 'fulfilled') {
          const allUsers = normalizeArray(usersResult.value?.data)
          const associatedUsers = allUsers.filter((user) => {
            const userClientId = pickValue(user, 'client_id', 'clientId', '')
            return String(userClientId) === String(clientId)
          })
          setUsersCount(associatedUsers.length)
        } else {
          setUsersCount(null)
        }
      } catch (fetchError) {
        setClient(null)
        setProducts([])
        setUsersCount(null)
        setError('No fue posible cargar el detalle del cliente.')
      } finally {
        setLoading(false)
      }
    }

    if (!clientId) {
      setError('No se encontró un identificador de cliente válido.')
      setLoading(false)
      return
    }

    fetchClientDetail()
  }, [clientId])

  const fullName = useMemo(
    () =>
      pickValue(
        client,
        'full_name_or_company',
        'fullNameOrCompany',
        '(Sin nombre)'
      ),
    [client]
  )
  const logo = useMemo(() => pickValue(client, 'logo', 'logo', ''), [client])
  const sector = useMemo(
    () => pickValue(client, 'sector_or_industry', 'sectorOrIndustry', ''),
    [client]
  )
  const email = useMemo(() => pickValue(client, 'email', 'email', '-'), [client])
  const phone = useMemo(
    () => pickValue(client, 'num_phone', 'numPhone', '-'),
    [client]
  )
  const clientType = useMemo(
    () => pickValue(client, 'client_type', 'clientType', 'No especificado'),
    [client]
  )
  const address = useMemo(() => {
  const rawAddress = pickValue(client, 'address', 'address', null)

  if (!rawAddress) return '-'

  if (typeof rawAddress === 'string') {
    return rawAddress.trim() || '-'
  }

  if (typeof rawAddress === 'object') {
    const parts = [
      rawAddress.street,
      rawAddress.numExt,
      rawAddress.city,
      rawAddress.zipCode,
      rawAddress.country,
    ]
      .map((part) => toSafeText(part, ''))
      .filter(Boolean)

    return parts.length > 0 ? parts.join(', ') : '-'
  }

  return toSafeText(rawAddress, '-')
}, [client])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !client) {
    return (
      <PrivatePageSection title="Detalle de Cliente">
        <Stack spacing={2}>
          <Alert severity="error">{error || 'Cliente no encontrado.'}</Alert>
          <Box>
            <Button variant="outlined" onClick={() => navigate('/clients')}>
              Volver a clientes
            </Button>
          </Box>
        </Stack>
      </PrivatePageSection>
    )
  }

  return (
    <PrivatePageSection
      title={fullName}
      subtitle="Ficha operativa de cliente"
      actions={
        <Button variant="outlined" onClick={() => navigate('/clients')}>
          Volver a clientes
        </Button>
      }
    >
      <Stack spacing={2.5}>
        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Avatar src={logo} sx={{ width: 64, height: 64 }} />
              <Box>
                <Typography variant="h6">{fullName}</Typography>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mt: 1 }}>
                  <Chip label={`Tipo: ${clientType}`} size="small" />
                  <Chip label={`Sector: ${sector || 'No especificado'}`} size="small" />
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Datos generales
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body2">Email: {email || '-'}</Typography>
                <Typography variant="body2">Teléfono: {phone || '-'}</Typography>
                <Typography variant="body2">Dirección: {address}</Typography>
                <Typography variant="body2">
                  ID Cliente: {pickValue(client, 'id', 'id', '-')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Conteos operativos
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body2">Productos asociados: {products.length}</Typography>
                <Typography variant="body2">
                  Usuarios asociados: {usersCount === null ? 'No disponible' : usersCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Productos asociados
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {products.length > 0 ? (
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {products.map((product) => {
                  const label = pickValue(
                    product,
                    'name_product',
                    'nameProduct',
                    '(Producto sin nombre)'
                  )
                  const productId = pickValue(product, 'id', 'id', null)
                  return (
                    <Chip key={`${productId || label}`} label={label} variant="outlined" />
                  )
                })}
              </Stack>
            ) : (
              <Alert severity="info">No hay productos asociados para este cliente.</Alert>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Información complementaria
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="body2" color="text.secondary">
              Esta ficha resume información operativa básica del cliente. No incluye analítica avanzada ni configuración administrativa.
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    </PrivatePageSection>
  )
}

export default ClientDetail
